import React, { useEffect } from "react";
import {
  LexicalComposer,
  type InitialConfigType,
} from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { ListNode, ListItemNode } from "@lexical/list";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { MarkNode } from "@lexical/mark";
import { HashtagNode } from "@lexical/hashtag";
import { OverflowNode } from "@lexical/overflow";
import "./text-editor.scss";
import ToolbarPlugin from "./tool-bar/tool-bar";
import ImagePlugin, { ImageNode } from "./plugins/image-plugin";
import ColorPlugin from "./plugins/color-plugin";
import TablePluginCustom from "./plugins/table-plugin";
import { Box, Divider } from "@mui/material";
import LoadHtmlPlugin from "./html-plugin/html-plugin";
import TableCellResizerPlugin from "./plugins/table-cell-resizer/table-cell-resizer";
import TableOfContentsPlugin from "./plugins/table-of-content-plugin/table-of-content-plugin";
import { $generateHtmlFromNodes } from "@lexical/html";
import { ExtendedTextNode } from "./extended-node";
import { MergeFieldPlugin } from "./plugins/MergeFieldPlugin";
import { MergeFieldNode } from "./merge-field-node";
import { MergeFieldTransformerPlugin } from "./MergeFieldTransformerPlugin";
import { SearchHighlightNode } from "./nodes/SearchHighlightNode";
import FindReplaceModal from "./find-replace-modal";
import {
  FindReplaceProvider,
  OPEN_FIND_REPLACE_COMMAND,
} from "./plugins/FindReplacePlugin";
import { FindReplaceKeyboardPlugin } from "./plugins/FindReplaceKeyboardPlugin";
import ReplaceMergeFieldsPlugin from "./replace-merge-fileds-plugin/replace-merge--fields-in-html";
// import { FindReplaceProvider } from "./plugins/FindReplacePlugin";
// import FindReplaceModal from "./find-replace-modal";

const theme = {
  paragraph: "editor-paragraph",
  quote: "editor-quote",
  // heading: {
  //   h1: "editor-heading-h1",
  //   h2: "editor-heading-h2",
  //   h3: "editor-heading-h3",
  //   h4: "editor-heading-h4",
  //   h5: "editor-heading-h5",
  //   h6: "editor-heading-h6",
  // },
  // list: {
  //   nested: {
  //     listitem: "editor-nested-listitem",
  //   },
  //   ol: "editor-list-ol",
  //   ul: "editor-list-ul",
  //   listitem: "editor-listitem",
  // },
  image: "editor-image",
  text: {
    bold: "editor-bold",
    underline: "editor-underline",
    strikethrough: "editor-strikethrough",
    underlineStrikethrough: "editor-underlineStrikethrough",
    italic: "editor-italic",
    code: "editor-code",
    highlight: "editor-highlight",
    subscript: "editor-subscript",
    superscript: "editor-superscript",
  },
  code: "editor-code",
  codeHighlight: {
    atrule: "editor-tokenAttr",
    attr: "editor-tokenAttr",
    boolean: "editor-tokenProperty",
    builtin: "editor-tokenSelector",
    cdata: "editor-tokenComment",
    char: "editor-tokenSelector",
    class: "editor-tokenFunction",
    "class-name": "editor-tokenFunction",
    comment: "editor-tokenComment",
    constant: "editor-tokenProperty",
    deleted: "editor-tokenProperty",
    doctype: "editor-tokenComment",
    entity: "editor-tokenOperator",
    function: "editor-tokenFunction",
    important: "editor-tokenVariable",
    inserted: "editor-tokenSelector",
    keyword: "editor-tokenAttr",
    namespace: "editor-tokenVariable",
    number: "editor-tokenProperty",
    operator: "editor-tokenOperator",
    prolog: "editor-tokenComment",
    property: "editor-tokenProperty",
    punctuation: "editor-tokenPunctuation",
    regex: "editor-tokenVariable",
    selector: "editor-tokenSelector",
    string: "editor-tokenSelector",
    symbol: "editor-tokenProperty",
    tag: "editor-tokenProperty",
    url: "editor-tokenOperator",
    variable: "editor-tokenVariable",
  },
  table: "editor-table",
  tableCell: "editor-tableCell",
  tableCellHeader: "editor-tableCellHeader",
  mark: "editor-mark",
  markOverlap: "editor-markOverlap",
  hashtag: "editor-hashtag",
};

const editorConfig: InitialConfigType = {
  namespace: "MyEditor",
  theme,
  onError: (error: Error) => {
    console.error("Lexical error:", error);
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    CodeHighlightNode,
    CodeNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    TableNode,
    TableCellNode,
    TableRowNode,
    HorizontalRuleNode,
    MarkNode,
    HashtagNode,
    OverflowNode,
    ImageNode,
    ExtendedTextNode,
    MergeFieldNode,
    SearchHighlightNode,
  ],
};

type OnChangePluginProps = {
  onChange: (html: any, newHtml: any) => void;
  onEditorInit?: (editor: any) => void;
};

const OnChangePlugin: React.FC<OnChangePluginProps> = ({
  onChange,
  onEditorInit,
}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        const json = JSON.stringify(editorState);
        onChange(json, html);
      });
    });
  }, [editor, onChange]);

  useEffect(() => {
    if (onEditorInit) {
      onEditorInit(editor); // Pass editor to parent
    }
  }, [editor, onEditorInit]);

  // const handleClick = () => {
  //   const editorState = editor.getEditorState();
  //   editorState.read(() => {
  //     const html = $generateHtmlFromNodes(editor, null);
  //     const json = JSON.stringify(editorState); // Optional if you want to store JSON too
  //     onChange(json, html);
  //   });
  // };

  // return (
  //   <button className="cancel-button next-button-wrapper" onClick={handleClick}>
  //     Save
  //   </button>
  // );
  return null;
};

interface LexicalEditorProps {
  defaultHtmlJson?: string;
  handleCancel?: any;
  handleSave?: any;
  handleDataChange?: any;
  onEditorInit?: (editor: any) => void;
  fieldName?: string;
  placeHolderValues?: any;
  templateMetaData?: any;
  replaceMergeFields?: boolean;
  includeOptOutText?: boolean;
}
const LexicalEditor: React.FC<LexicalEditorProps> = ({
  handleDataChange,
  defaultHtmlJson,
  onEditorInit,
  fieldName,
  placeHolderValues,
  templateMetaData,
  replaceMergeFields = false,
  includeOptOutText = false
}) => {
  const [isFullscreen, setIsFullscreen] = React.useState<boolean>(false);
  const handleHTml = (children: any, htmlText: any) => {
    handleDataChange(htmlText, children);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  // Add/remove fullscreen-active class to body when fullscreen changes
  React.useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('fullscreen-active');
    } else {
      document.body.classList.remove('fullscreen-active');
    }
    return () => document.body.classList.remove('fullscreen-active');
  }, [isFullscreen]);

  return (
    <Box
      sx={{
        position: "relative",
        height: isFullscreen ? "100vh" : "auto",
        width: isFullscreen ? "100vw" : "100%",
        zIndex: isFullscreen ? 1300 : "auto", // above modals/dialogs
        top: 0,
        left: 0,
        bgcolor: "white",
        ...(isFullscreen && {
          position: "fixed",
          p: 2,
        }),
      }}
      className={`${isFullscreen ? "fullscreen-editor" : ""}`}
    >
      <LexicalComposer initialConfig={editorConfig}>
        {/* <EnhancedToolbarPlugin /> */}
        <FindReplaceProvider>
          <ToolbarPlugin
            toggleFullscreen={toggleFullscreen}
            isFullscreen={isFullscreen}
            fieldName={fieldName}
            includeOptOutText={includeOptOutText}
            templateMetaData={templateMetaData}
          />
          <Divider />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          <TablePlugin />
          <CheckListPlugin />
          <TabIndentationPlugin />
          <ImagePlugin />
          <ColorPlugin />
          <TablePluginCustom />
          <TableOfContentsPlugin />
          <TableCellResizerPlugin />
          <MergeFieldPlugin />
          <MergeFieldTransformerPlugin />
          <FindReplaceKeyboardPlugin />
          <LoadHtmlPlugin html={defaultHtmlJson || ""} shouldLoad={true} />
          {replaceMergeFields && (
            <ReplaceMergeFieldsPlugin
              placeHolderValues={placeHolderValues}
              templateMetaData={templateMetaData}
            />
          )}
          <div className="editor-container">
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={
                <div className="editor-placeholder">Enter text...</div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              {/* <button className="cancel-button" onClick={handleCancel}>
                Cancel
              </button> */}
              <OnChangePlugin
                onChange={handleHTml}
                onEditorInit={onEditorInit}
              />
            </Box>
          </div>
          <FindReplaceModal />
        </FindReplaceProvider>
      </LexicalComposer>
    </Box>
  );
};

export default LexicalEditor;
